import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Button, Grid, Loader } from "semantic-ui-react";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { GridPager } from "../../common/pagination/GridPager";
import PayrollDataGrid, { payrollActionsCellClass, payrollActionsHeaderClass } from "./PayrollDataGrid";
import PayrollModal from "./PayrollModal";
import {
  bonusExportBasename,
  buildBonusExportQuery,
  fetchBonusExportCsv,
  fetchBonusExportExcel,
  fetchBonusExportPdf,
} from "./bonusPolicyApi";
import { axiosBasic } from "../../../api/axios";
import { triggerBlobDownload } from "../timesheets/timesheetApi";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "./Payroll.css";

const PAGE_SIZE = 8;
const { BonusPolicy, Department, SalaryComponent } = config.REST_API;

/** Matches C# PayrollStructureLineAmountType */
const AmountType = { Fixed: 0, PercentOfGross: 1 };

const FILTER = {
  ALL: "all",
  SCOPE_ALL_EMPLOYEES: "allEmployees",
  SCOPE_SELECTED: "selected",
  STATUS_ACTIVE: "active",
  STATUS_INACTIVE: "inactive",
};

function bonusMatchesFilters(row, { search, amountType, departmentId, officeId, scope, status }) {
  if (search) {
    const q = search.toLowerCase();
    if (!String(row.name ?? "").toLowerCase().includes(q)) return false;
  }
  if (amountType !== FILTER.ALL) {
    const want = Number(amountType);
    if (row.amountType !== want) return false;
  }
  if (departmentId !== FILTER.ALL) {
    const deptId = Number(departmentId);
    if (!row.applyToAllEmployees && !row.departmentIds.includes(deptId)) return false;
  }
  if (officeId !== FILTER.ALL) {
    const offId = Number(officeId);
    if (!row.applyToAllEmployees && !row.officeIds.includes(offId)) return false;
  }
  if (scope === FILTER.SCOPE_ALL_EMPLOYEES && !row.applyToAllEmployees) return false;
  if (scope === FILTER.SCOPE_SELECTED && !row.applyToSelectedDepartmentsAndOffices) return false;
  if (status === FILTER.STATUS_ACTIVE && !row.isActive) return false;
  if (status === FILTER.STATUS_INACTIVE && row.isActive) return false;
  return true;
}

function unwrapArray(res) {
  const body = res?.data;
  if (body == null || body.success === false) return { items: [], message: body?.message };
  if (Array.isArray(body)) return { items: body, message: null };
  if (Array.isArray(body.data)) return { items: body.data, message: null };
  return { items: [], message: null };
}

function unwrapOne(res) {
  const body = res?.data;
  if (body == null || body.success === false) return null;
  if (body.data && typeof body.data === "object") return body.data;
  if (typeof body === "object" && !Array.isArray(body)) return body;
  return null;
}

function normalizeBonus(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  const amountTypeNum = Number(get("amountType", "AmountType") ?? 0);
  return {
    id: String(get("id", "Id") ?? ""),
    name: String(get("name", "Name") ?? ""),
    amountType: amountTypeNum === AmountType.PercentOfGross ? AmountType.PercentOfGross : AmountType.Fixed,
    amountValue: Number(get("amountValue", "AmountValue") ?? 0),
    applyToAllEmployees: Boolean(get("applyToAllEmployees", "ApplyToAllEmployees")),
    applyToSelectedDepartmentsAndOffices: Boolean(
      get("applyToSelectedDepartmentsAndOffices", "ApplyToSelectedDepartmentsAndOffices")
    ),
    minimumServiceMonths:
      get("minimumServiceMonths", "MinimumServiceMonths") != null && get("minimumServiceMonths", "MinimumServiceMonths") !== ""
        ? Number(get("minimumServiceMonths", "MinimumServiceMonths"))
        : null,
    departmentIds: Array.isArray(get("departmentIds", "DepartmentIds"))
      ? get("departmentIds", "DepartmentIds").map((x) => Number(x))
      : [],
    officeIds: Array.isArray(get("officeIds", "OfficeIds")) ? get("officeIds", "OfficeIds").map((x) => Number(x)) : [],
    scopeSummary: String(get("scopeSummary", "ScopeSummary") ?? ""),
    eligibleEmployeeCount: Number(get("eligibleEmployeeCount", "EligibleEmployeeCount") ?? 0),
    isActive: get("isActive", "IsActive") !== false,
  };
}

function formatAmountDisplay(row) {
  if (row.amountType === AmountType.PercentOfGross) {
    return `${row.amountValue}%`;
  }
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(row.amountValue);
}

function buildSavePayload(form) {
  const minRaw = String(form.minServiceStr ?? "").trim();
  const minParsed = minRaw === "" ? null : parseInt(minRaw, 10);
  return {
    Id: form.id || undefined,
    Name: form.name.trim(),
    AmountType: form.amountType,
    AmountValue: Number(form.amountValue),
    ApplyToAllEmployees: form.applyToAll,
    ApplyToSelectedDepartmentsAndOffices: form.applyToSelected,
    MinimumServiceMonths: Number.isFinite(minParsed) && minParsed >= 0 ? minParsed : null,
    DepartmentIds: form.applyToSelected ? form.departmentIds : [],
    OfficeIds: form.applyToSelected ? form.officeIds : [],
    IsActive: true,
  };
}

const emptyForm = () => ({
  id: "",
  name: "",
  amountType: AmountType.Fixed,
  amountValue: "",
  applyToAll: true,
  applyToSelected: false,
  departmentIds: [],
  officeIds: [],
  minServiceStr: "",
});

const BonusesPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(/** @type {"create" | "edit"} */ ("create"));
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [eligiblePreview, setEligiblePreview] = useState(/** @type {number | null} */ (null));
  const [eligibleLoading, setEligibleLoading] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [amountTypeFilter, setAmountTypeFilter] = useState(FILTER.ALL);
  const [departmentFilter, setDepartmentFilter] = useState(FILTER.ALL);
  const [officeFilter, setOfficeFilter] = useState(FILTER.ALL);
  const [scopeFilter, setScopeFilter] = useState(FILTER.ALL);
  const [statusFilter, setStatusFilter] = useState(FILTER.ALL);
  const [exportLoading, setExportLoading] = useState(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, amountTypeFilter, departmentFilter, officeFilter, scopeFilter, statusFilter]);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axiosBasic.get(BonusPolicy.Get);
      const { items, message } = unwrapArray(res);
      if (message) {
        setRows([]);
        setLoadError(message);
      } else {
        setRows(items.map(normalizeBonus));
        setPage(1);
      }
    } catch (e) {
      setRows([]);
      setLoadError(e?.response?.data?.message || e?.message || t("app.payroll.bonuses.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [depRes, offRes] = await Promise.all([
          axiosBasic.get(Department.Get),
          axiosBasic.get(SalaryComponent.Offices),
        ]);
        const depBody = depRes.data || {};
        if (depBody.success && Array.isArray(depBody.data)) {
          setDepartments(
            depBody.data
              .map((d) => ({ id: d.id, name: d.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
        const offBody = offRes.data || {};
        if (offBody.success && Array.isArray(offBody.data)) {
          setOffices(
            offBody.data
              .map((o) => ({
                id: o.id,
                name: o.countryName ? `${o.name} (${o.countryName})` : o.name,
              }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
      } catch {
        setDepartments([]);
        setOffices([]);
      }
    };
    loadLookups();
  }, []);

  const filteredRows = useMemo(
    () =>
      rows.filter((row) =>
        bonusMatchesFilters(row, {
          search: debouncedSearch,
          amountType: amountTypeFilter,
          departmentId: departmentFilter,
          officeId: officeFilter,
          scope: scopeFilter,
          status: statusFilter,
        })
      ),
    [rows, debouncedSearch, amountTypeFilter, departmentFilter, officeFilter, scopeFilter, statusFilter]
  );

  const total = filteredRows.length;
  const slice = useMemo(
    () => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRows, page]
  );

  const hasActiveFilters =
    debouncedSearch !== "" ||
    amountTypeFilter !== FILTER.ALL ||
    departmentFilter !== FILTER.ALL ||
    officeFilter !== FILTER.ALL ||
    scopeFilter !== FILTER.ALL ||
    statusFilter !== FILTER.ALL;

  const clearFilters = () => {
    setSearchQuery("");
    setAmountTypeFilter(FILTER.ALL);
    setDepartmentFilter(FILTER.ALL);
    setOfficeFilter(FILTER.ALL);
    setScopeFilter(FILTER.ALL);
    setStatusFilter(FILTER.ALL);
  };

  const exportQueryArgs = useMemo(
    () =>
      buildBonusExportQuery({
        search: debouncedSearch,
        amountType: amountTypeFilter,
        departmentId: departmentFilter,
        officeId: officeFilter,
        scope: scopeFilter,
        status: statusFilter,
      }),
    [debouncedSearch, amountTypeFilter, departmentFilter, officeFilter, scopeFilter, statusFilter]
  );

  const runExportExcel = async () => {
    setExportLoading("excel");
    try {
      const blob = await fetchBonusExportExcel(exportQueryArgs);
      triggerBlobDownload(blob, `${bonusExportBasename()}.xlsx`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.bonuses.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const runExportCsv = async () => {
    setExportLoading("csv");
    try {
      const blob = await fetchBonusExportCsv(exportQueryArgs);
      triggerBlobDownload(blob, `${bonusExportBasename()}.csv`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.bonuses.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const runExportPdf = async () => {
    setExportLoading("pdf");
    try {
      const blob = await fetchBonusExportPdf(exportQueryArgs);
      triggerBlobDownload(blob, `${bonusExportBasename()}.pdf`);
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.bonuses.exportFailed"));
    } finally {
      setExportLoading(null);
    }
  };

  const openCreate = () => {
    setModalMode("create");
    setForm(emptyForm());
    setEligiblePreview(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setForm({
      id: row.id,
      name: row.name,
      amountType: row.amountType,
      amountValue: String(row.amountValue),
      applyToAll: row.applyToAllEmployees,
      applyToSelected: row.applyToSelectedDepartmentsAndOffices,
      departmentIds: [...row.departmentIds],
      officeIds: [...row.officeIds],
      minServiceStr: row.minimumServiceMonths != null ? String(row.minimumServiceMonths) : "",
    });
    setEligiblePreview(row.eligibleEmployeeCount);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm());
    setEligiblePreview(null);
    setEligibleLoading(false);
  };

  const previewPayload = useMemo(() => buildSavePayload(form), [form]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const canPreview =
      (form.applyToAll || form.applyToSelected) &&
      (!form.applyToSelected || form.departmentIds.length > 0 || form.officeIds.length > 0);
    if (!canPreview) {
      setEligiblePreview(null);
      return undefined;
    }
    const id = window.setTimeout(async () => {
      setEligibleLoading(true);
      try {
        const res = await axiosBasic.post(BonusPolicy.PreviewEligible, previewPayload);
        const data = unwrapOne(res);
        const count = data?.eligibleEmployeeCount ?? data?.EligibleEmployeeCount;
        setEligiblePreview(Number.isFinite(Number(count)) ? Number(count) : 0);
      } catch {
        setEligiblePreview(null);
      } finally {
        setEligibleLoading(false);
      }
    }, 400);
    return () => window.clearTimeout(id);
  }, [modalOpen, previewPayload, form.applyToAll, form.applyToSelected, form.departmentIds, form.officeIds]);

  const canSave =
    form.name.trim() !== "" &&
    form.amountValue !== "" &&
    Number(form.amountValue) >= 0 &&
    (form.applyToAll || form.applyToSelected) &&
    (!form.applyToSelected || form.departmentIds.length > 0 || form.officeIds.length > 0) &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await axiosBasic.post(BonusPolicy.Save, buildSavePayload(form));
      const body = res?.data;
      if (body && body.success) {
        Common.showSuccessMessage(t("app.payroll.bonuses.saveSuccess"));
        closeModal();
        await loadGrid();
      } else {
        Common.showErrorMessage(body?.message || t("app.payroll.bonuses.saveError"));
      }
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.bonuses.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const toggleDepartment = (deptId) => {
    setForm((prev) => {
      const ids = prev.departmentIds.includes(deptId)
        ? prev.departmentIds.filter((x) => x !== deptId)
        : [...prev.departmentIds, deptId];
      return { ...prev, departmentIds: ids };
    });
  };

  const toggleOffice = (officeId) => {
    setForm((prev) => {
      const ids = prev.officeIds.includes(officeId)
        ? prev.officeIds.filter((x) => x !== officeId)
        : [...prev.officeIds, officeId];
      return { ...prev, officeIds: ids };
    });
  };

  const columns = useMemo(
    () => [
      { key: "name", label: t("app.payroll.bonuses.colName"), sortable: false },
      { key: "amountType", label: t("app.payroll.bonuses.colAmountType"), sortable: false },
      { key: "value", label: t("app.payroll.bonuses.colValue"), sortable: false },
      { key: "scope", label: t("app.payroll.bonuses.colScope"), sortable: false },
      { key: "minService", label: t("app.payroll.bonuses.colMinService"), sortable: false },
      { key: "eligible", label: t("app.payroll.bonuses.colEligible"), sortable: false },
      { key: "actions", label: t("app.payroll.common.actions"), sortable: false, headerClassName: payrollActionsHeaderClass },
    ],
    [t]
  );

  return (
    <div className="documents-page payroll-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-gift" />}
        title={t("app.payroll.bonuses.title")}
        description={t("app.payroll.bonuses.subtitle")}
        rightContent={
          <Button primary className="rounded-pill" type="button" onClick={openCreate}>
            <i className="bi bi-plus-lg me-1" aria-hidden />
            {t("app.payroll.bonuses.new")}
          </Button>
        }
      />

      <Grid stackable className="mb-3">
        <Grid.Row>
          <Grid.Column width={16}>
            <section className="ts-appr-stats">
              <div className="ts-appr-stat-card ts-appr-stat-card--total">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-stars" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">{loading ? "—" : total}</span>
                      <span className="ts-appr-stat-card__label">{t("app.payroll.bonuses.statTypes")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {loadError && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3" role="alert">
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadGrid}>
            {t("app.payroll.common.retry")}
          </button>
        </div>
      )}

      <section className="ts-time-log-shell mb-3" aria-label={t("app.payroll.bonuses.filtersAria")}>
        <div className="ts-appr-filters" role="search">
          <div className="ts-appr-filters__title text-muted" id="payroll-bn-filters-heading">
            {t("app.payroll.bonuses.filtersToolbarLabel")}
          </div>

          <div className="ts-appr-filters__toolbar" aria-labelledby="payroll-bn-filters-heading">
            <div className="ts-appr-filters__toolbar-left">
              <div
                className="ts-time-log-export-group ts-time-log-export-group--labeled ts-appr-filters__exports"
                role="group"
                aria-label={t("app.payroll.bonuses.exportActionsAria")}
              >
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--excel ts-tl-export-icon--labeled"
                  onClick={() => runExportExcel()}
                  disabled={Boolean(exportLoading) || loading}
                  title={t("app.payroll.bonuses.exportExcelTitle")}
                >
                  {exportLoading === "excel" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-file-earmark-spreadsheet" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportExcelShort")}</span>
                </button>
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--csv ts-tl-export-icon--labeled"
                  onClick={() => runExportCsv()}
                  disabled={Boolean(exportLoading) || loading}
                  title={t("app.payroll.bonuses.exportCsvTitle")}
                >
                  {exportLoading === "csv" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-filetype-csv" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportCsvShort")}</span>
                </button>
                <button
                  type="button"
                  className="ts-tl-export-icon ts-tl-export-icon--pdf ts-tl-export-icon--labeled"
                  onClick={() => runExportPdf()}
                  disabled={Boolean(exportLoading) || loading}
                  title={t("app.payroll.bonuses.exportPdfTitle")}
                >
                  {exportLoading === "pdf" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    <i className="bi bi-file-earmark-pdf" aria-hidden />
                  )}
                  <span className="ts-tl-export-label">{t("app.timesheets.common.exportPdfShort")}</span>
                </button>
              </div>
            </div>
            <p className="ts-appr-filters__toolbar-hint ts-appr-controls__hint mb-0">
              {t("app.payroll.bonuses.filtersScopeHint")}
            </p>
          </div>

          <div className="ts-appr-filters__fields ts-appr-filters__fields--bonuses">
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-bn-search">
                {t("app.payroll.bonuses.searchBonus")}
              </label>
              <input
                id="payroll-bn-search"
                type="search"
                autoComplete="off"
                placeholder={t("app.payroll.bonuses.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-bn-filter-amt-type">
                {t("app.payroll.bonuses.filterAmountType")}
              </label>
              <select
                id="payroll-bn-filter-amt-type"
                value={amountTypeFilter}
                onChange={(e) => setAmountTypeFilter(e.target.value)}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.payroll.bonuses.filterAllAmountTypes")}</option>
                <option value={String(AmountType.Fixed)}>{t("app.payroll.bonuses.typeFixed")}</option>
                <option value={String(AmountType.PercentOfGross)}>{t("app.payroll.bonuses.typePercentGross")}</option>
              </select>
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-bn-filter-dept">
                {t("app.timesheets.approvals.filterDepartment")}
              </label>
              <select
                id="payroll-bn-filter-dept"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.timesheets.approvals.filterAllDepartments")}</option>
                {departments.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-bn-filter-office">
                {t("app.timesheets.approvals.filterOffice")}
              </label>
              <select
                id="payroll-bn-filter-office"
                value={officeFilter}
                onChange={(e) => setOfficeFilter(e.target.value)}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.timesheets.approvals.filterAllOffices")}</option>
                {offices.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ts-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-bn-filter-scope">
                {t("app.payroll.bonuses.filterScopeType")}
              </label>
              <select
                id="payroll-bn-filter-scope"
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value)}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.payroll.bonuses.filterScopeAny")}</option>
                <option value={FILTER.SCOPE_ALL_EMPLOYEES}>{t("app.payroll.bonuses.filterScopeAllEmployees")}</option>
                <option value={FILTER.SCOPE_SELECTED}>{t("app.payroll.bonuses.filterScopeSelected")}</option>
              </select>
            </div>
            <div className="ts-filter-field ts-appr-filters__filter--status">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-bn-filter-status">
                {t("app.payroll.bonuses.filterStatus")}
              </label>
              <select
                id="payroll-bn-filter-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={loading}
              >
                <option value={FILTER.ALL}>{t("app.payroll.bonuses.filterAnyStatus")}</option>
                <option value={FILTER.STATUS_ACTIVE}>{t("app.payroll.bonuses.filterActive")}</option>
                <option value={FILTER.STATUS_INACTIVE}>{t("app.payroll.bonuses.filterInactive")}</option>
              </select>
            </div>
            <div className="ts-filter-field ts-appr-filters__clear-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-bn-clear-filters">
                {t("app.payroll.bonuses.clearFiltersLabel")}
              </label>
              <button
                id="payroll-bn-clear-filters"
                type="button"
                className="btn btn-outline-secondary ts-tl-clear-filters-btn ts-appr-filters__clear-btn"
                onClick={clearFilters}
                disabled={loading || !hasActiveFilters}
              >
                <i className="bi bi-x-lg me-1" aria-hidden />
                {t("app.payroll.bonuses.clearFilters")}
              </button>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Loader active inline="centered">
            {t("app.payroll.bonuses.loadingGrid")}
          </Loader>
        </div>
      ) : (
        <>
          {hasActiveFilters ? (
            <p className="documents-page-description ts-appr-results-hint mb-3">
              {t("app.payroll.bonuses.resultsFilteredHint", { shown: total, total: rows.length })}
            </p>
          ) : null}

          <PayrollDataGrid
            columns={columns}
            rows={slice}
            getRowKey={(row) => String(row.id)}
            emptyMessage={t("app.payroll.common.noResults")}
            renderRow={(row) => (
              <>
                <td className="fw-semibold">{row.name}</td>
                <td>
                  {row.amountType === AmountType.PercentOfGross
                    ? t("app.payroll.bonuses.typePercentGross")
                    : t("app.payroll.bonuses.typeFixed")}
                </td>
                <td>{formatAmountDisplay(row)}</td>
                <td>{row.scopeSummary || (row.applyToAllEmployees ? t("app.payroll.bonuses.scopeAll") : "—")}</td>
                <td>{row.minimumServiceMonths != null ? row.minimumServiceMonths : "—"}</td>
                <td>{row.eligibleEmployeeCount}</td>
                <td className={payrollActionsCellClass}>
                  <div className="documents-actions-inline d-flex flex-wrap gap-1">
                    <button
                      type="button"
                      className="documents-grid-action-btn documents-grid-action-btn--edit documents-action-compact"
                      onClick={() => history.push(`/payroll/bonuses/generate/${encodeURIComponent(row.id)}`)}
                    >
                      {t("app.payroll.bonuses.generateAction")}
                    </button>
                    <button
                      type="button"
                      className="documents-grid-action-btn documents-grid-action-btn--edit documents-action-compact"
                      onClick={() => openEdit(row)}
                    >
                      {t("app.payroll.common.edit")}
                    </button>
                  </div>
                </td>
              </>
            )}
          />

          <div className="documents-pagination documents-pagination--grid-pager d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
            <span className="documents-pagination-total text-muted small">
              {total === 0
                ? t("app.payroll.common.noResults")
                : t("app.payroll.common.showingRange", {
                    from: (page - 1) * PAGE_SIZE + 1,
                    to: Math.min(page * PAGE_SIZE, total),
                    total,
                  })}
            </span>
            <GridPager
              className="documents-rc-pagination"
              onChange={(p) => setPage(p)}
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              showLessItems
              showTitle={false}
              showSizeChanger={false}
            />
          </div>
        </>
      )}

      <PayrollModal
        open={modalOpen}
        onClose={closeModal}
        title={modalMode === "create" ? t("app.payroll.bonuses.modalTitle") : t("app.payroll.bonuses.modalEditTitle")}
        titleId="payroll-bonus-modal-title"
        footer={
          <>
            <button type="button" className="att-btn att-btn--ghost" onClick={closeModal} disabled={saving}>
              {t("app.payroll.common.cancel")}
            </button>
            <button type="button" className="att-btn att-btn--save-primary" onClick={handleSave} disabled={!canSave}>
              {saving ? t("app.common.loading") : t("app.payroll.common.save")}
            </button>
          </>
        }
      >
        <div className="d-flex flex-column gap-3">
          <div>
            <label className="att-modal-field-label" htmlFor="payroll-bn-name">
              {t("app.payroll.bonuses.fieldBonusName")}
            </label>
            <input
              id="payroll-bn-name"
              className="form-control"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="att-modal-field-label" htmlFor="payroll-bn-amt-type">
              {t("app.payroll.bonuses.fieldAmountType")}
            </label>
            <select
              id="payroll-bn-amt-type"
              className="form-select"
              value={form.amountType}
              onChange={(e) => setForm((p) => ({ ...p, amountType: Number(e.target.value) }))}
            >
              <option value={AmountType.Fixed}>{t("app.payroll.bonuses.typeFixed")}</option>
              <option value={AmountType.PercentOfGross}>{t("app.payroll.bonuses.typePercentGross")}</option>
            </select>
          </div>
          <div>
            <label className="att-modal-field-label" htmlFor="payroll-bn-amt">
              {t("app.payroll.bonuses.fieldAmount")}
            </label>
            <input
              id="payroll-bn-amt"
              type="number"
              min="0"
              step="any"
              className="form-control"
              placeholder="0"
              value={form.amountValue}
              onChange={(e) => setForm((p) => ({ ...p, amountValue: e.target.value }))}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <div className="form-check">
              <input
                id="payroll-bn-all"
                type="checkbox"
                className="form-check-input"
                checked={form.applyToAll}
                onChange={() =>
                  setForm((p) => ({
                    ...p,
                    applyToAll: true,
                    applyToSelected: false,
                    departmentIds: [],
                    officeIds: [],
                  }))
                }
              />
              <label className="form-check-label" htmlFor="payroll-bn-all">
                {t("app.payroll.bonuses.applyToAll")}
              </label>
            </div>
            <div className="form-check">
              <input
                id="payroll-bn-selected"
                type="checkbox"
                className="form-check-input"
                checked={form.applyToSelected}
                onChange={() =>
                  setForm((p) => ({
                    ...p,
                    applyToAll: false,
                    applyToSelected: true,
                  }))
                }
              />
              <label className="form-check-label" htmlFor="payroll-bn-selected">
                {t("app.payroll.bonuses.applyToSelected")}
              </label>
            </div>
          </div>

          {form.applyToSelected ? (
            <>
              <div>
                <span className="att-modal-field-label d-block" id="payroll-bn-dept-label">
                  {t("app.payroll.bonuses.fieldDepartment")}
                </span>
                <div
                  className="documents-office-checkbox-list payroll-bonus-scope-list"
                  role="group"
                  aria-labelledby="payroll-bn-dept-label"
                >
                  {departments.length === 0 ? (
                    <p className="text-muted small mb-0">{t("app.payroll.common.noResults")}</p>
                  ) : (
                    departments.map((d) => (
                      <label key={d.id} className="documents-office-checkbox-row">
                        <input
                          type="checkbox"
                          checked={form.departmentIds.includes(d.id)}
                          disabled={saving}
                          onChange={() => toggleDepartment(d.id)}
                        />
                        <span>{d.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-muted small mt-1 mb-0">{t("app.payroll.bonuses.selectDepartment")}</p>
              </div>
              <div>
                <span className="att-modal-field-label d-block" id="payroll-bn-office-label">
                  {t("app.payroll.bonuses.fieldOffice")}
                </span>
                <div
                  className="documents-office-checkbox-list payroll-bonus-scope-list"
                  role="group"
                  aria-labelledby="payroll-bn-office-label"
                >
                  {offices.length === 0 ? (
                    <p className="text-muted small mb-0">{t("app.payroll.common.noResults")}</p>
                  ) : (
                    offices.map((o) => (
                      <label key={o.id} className="documents-office-checkbox-row">
                        <input
                          type="checkbox"
                          checked={form.officeIds.includes(o.id)}
                          disabled={saving}
                          onChange={() => toggleOffice(o.id)}
                        />
                        <span>{o.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-muted small mt-1 mb-0">{t("app.payroll.bonuses.selectOffice")}</p>
              </div>
            </>
          ) : null}

          <div>
            <label className="att-modal-field-label" htmlFor="payroll-bn-service">
              {t("app.payroll.bonuses.fieldMinService")}
            </label>
            <input
              id="payroll-bn-service"
              type="number"
              min="0"
              className="form-control"
              placeholder={t("app.payroll.bonuses.fieldMinServicePh")}
              value={form.minServiceStr}
              onChange={(e) => setForm((p) => ({ ...p, minServiceStr: e.target.value }))}
            />
            {eligibleLoading ? (
              <p className="text-muted small mt-2 mb-0">{t("app.payroll.bonuses.eligiblePreviewLoading")}</p>
            ) : eligiblePreview != null ? (
              <p className="text-muted small mt-2 mb-0">
                {t("app.payroll.bonuses.eligiblePreview", { count: eligiblePreview })}
              </p>
            ) : null}
          </div>
        </div>
      </PayrollModal>
    </div>
  );
};

export default BonusesPage;
